import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

export const AuthPageRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  // A19: stack form panel above brand panel on mobile (<900px)
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

export const FormPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  flex: "1",
  minHeight: "100vh",
  // A24: gradient color stops match mockup (3 distinct stops, center = base bg)
  display: "flex",
  flexDirection: "column",
  borderRight: `1px solid ${theme.palette.divider}`,
  // A25: local grid overlay (::before) matching mockup form-panel::before
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage: `linear-gradient(${theme.palette.mode === "dark" ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)"} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)"} 1px, transparent 1px)`,
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },
  // A19/A20: full-width form panel with reduced padding on mobile
  [theme.breakpoints.down("md")]: {
    flex: "0 0 auto",
    minHeight: "auto",
    borderRight: "none",
  },
}));

export const FormArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 80px 60px",
  position: "relative",
  zIndex: 1,
  // A20: reduced form area padding on mobile
  [theme.breakpoints.down("md")]: {
    padding: "24px",
  },
}));

export const FormCard = styled(Box)(({ theme }) => ({
  width: 400,
  padding: 40,
  borderRadius: "16px",
  // A1/A2: translucent surface with backdrop blur (matches mockup form-wrap)
  background:
    theme.palette.mode === "dark"
      ? "rgba(30, 41, 59, 0.72)"
      : "rgba(255, 255, 255, 0.72)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  position: "relative",
  zIndex: 1,
}));

export const AuthFooter = styled(Box)(({ theme }) => ({
  padding: "0 80px 30px",
  textAlign: "center",
  position: "relative",
  zIndex: 1,
  color: theme.palette.text.secondary,
  fontSize: "0.6875rem",
  // A20: reduced footer padding on mobile
  [theme.breakpoints.down("md")]: {
    padding: "0 24px 24px",
  },
}));

// Brand panel components — theme-aware (light/dark SVG variants swap in Layout.tsx)
export const AuthGraphicSection = styled(Box)(({ theme }) => ({
  position: "relative",
  flex: 1,
  minHeight: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "48px 48px 120px",
  background:
    theme.palette.mode === "dark"
      ? `radial-gradient(circle at 70% 40%, ${theme.palette.grey[800]} 0%, ${theme.palette.background.default} 50%, ${theme.palette.grey[900]} 100%)`
      : `radial-gradient(circle at 70% 40%, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[50]} 50%, ${theme.palette.background.paper} 100%)`,
  backgroundSize: "100% 100%",
  borderLeft: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
  // A25: local grid overlay (::before) matching mockup brand-panel::before
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage: `linear-gradient(${theme.palette.mode === "dark" ? "rgba(255,255,255,0.012)" : "rgba(0,0,0,0.012)"} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.012)" : "rgba(0,0,0,0.012)"} 1px, transparent 1px)`,
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },
  // A19: brand panel stacks below the form on mobile (<900px)
  [theme.breakpoints.down("md")]: {
    flex: "0 0 auto",
    minHeight: "auto",
    borderLeft: "none",
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: "40px 24px",
  },
}));

export const ContentWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
  maxWidth: 440,
  textAlign: "center",
  position: "relative",
  zIndex: 1,
  margin: "0 auto",
}));

export const Wordmark = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 800,
  fontSize: "3rem",
  letterSpacing: "-0.03em",
  textTransform: "lowercase",
  lineHeight: "72px",
}));

export const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: "0.6875rem",
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  marginTop: "2px",
}));

export const Tagline = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "1.125rem",
  fontWeight: 400,
  maxWidth: 380,
  lineHeight: 1.6,
}));

export const TrustBar = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 40,
  left: 0,
  right: 0,
  textAlign: "center",
  color: theme.palette.text.disabled,
  fontSize: 10,
  letterSpacing: "0.06em",
  zIndex: 3,
}));
